import type { DefaultGlobalContext, PluginConfigSet, PluginRepository, TunaPlugin } from '@tunasong/plugin-lib'
import type { EntityOrElement } from '@tunasong/schemas'
import { configurePlugins } from './plugin-collection.js'
import { pluginSets } from './plugins.js'

/** We want to dynamically load plugins when used */

export class Repository implements PluginRepository {
  #pluginMap = new Map<string, TunaPlugin<EntityOrElement, DefaultGlobalContext>>()
  #pluginCollections: Partial<Record<string, TunaPlugin<EntityOrElement, DefaultGlobalContext>[]>> = {}
  #pluginCollectionPromises: Partial<Record<string, Promise<TunaPlugin<EntityOrElement, DefaultGlobalContext>[]>>> = {}

  /**
   * Returns the SAME promise for a collection until it settles, so it is safe to pass to
   * React's `use()` during render. Not `async`: an async function would wrap the memoized
   * promise in a new one on every call.
   *
   * A rejected load is forgotten, so the next call retries. A chunk-load failure after a deploy
   * or on a flaky network used to reject the memoized promise for good, breaking every route
   * that needs plugins until a full page reload (GH #1914).
   */
  loadCollection = (type: PluginConfigSet): Promise<TunaPlugin<EntityOrElement, DefaultGlobalContext>[]> => {
    const memoized = this.#pluginCollectionPromises[type]
    if (memoized) {
      return memoized
    }

    const pluginsPromise = configurePlugins(pluginSets[type]).then(
      coll => {
        this.#pluginCollections[type] = coll

        // We special case 'all' to load all plugins into the map
        if (type === 'all') {
          for (const plugin of coll) {
            this.#pluginMap.set(plugin.key, plugin)
          }
        }
        return coll
      },
      (error: unknown) => {
        delete this.#pluginCollectionPromises[type]
        throw error
      }
    )
    this.#pluginCollectionPromises[type] = pluginsPromise

    return pluginsPromise
  }

  getCollection = (type: PluginConfigSet) => this.#pluginCollections[type]
  getPlugin = (key: string) => this.#pluginMap.get(key)
}
